/** Classify step — sort items into categories by selecting an item then tapping a category. */
import { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Check, X, RotateCcw } from 'lucide-react';
import { useSession } from '../SessionContext';
import { AnimatedKbd, useShortcutOverlay } from '../components/ShortcutOverlay';
import { StepShell } from './StepShell';
import {
  PromptRenderer,
  type TiptapNode,
  type MediaItem,
} from '@/shared/components/PromptRenderer';
import { normaliseMediaRefs } from '../utils/normaliseMediaRefs';
import { FeedbackDisplay } from './FeedbackDisplay';
import type { ClassifyConfig } from '../types';

interface DiagramJson {
  syntax: string;
  diagramType?: string;
}

type ClassifyCategory = { id: string; label: string };
type ClassifyItem = { id: string; text: string; categoryId: string };

const EMPTY_CATEGORIES: ClassifyCategory[] = [];
const EMPTY_ITEMS: ClassifyItem[] = [];
const EMPTY_ASSIGNMENTS: Record<string, string> = {};

/**
 * Classify step — sort items into categories.
 * Tap an item to select it, then tap a category to place it.
 * Tap a classified item to return it to the pool.
 */
export function ClassifyStep() {
  const { currentStep, state, dispatch } = useSession();
  const { showShortcuts } = useShortcutOverlay();
  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);
  const [hoveredCategoryId, setHoveredCategoryId] = useState<string | null>(null);

  const snapshot = currentStep?.stepSnapshotPublicJson;
  const config = (snapshot?.config ?? null) as unknown as ClassifyConfig | null;
  const categories = config?.categories ?? EMPTY_CATEGORIES;
  const items = config?.items ?? EMPTY_ITEMS;

  const answer = state.answer?.type === 'classify' ? state.answer : null;
  const assignments = answer?.assignments ?? EMPTY_ASSIGNMENTS;
  const phase = state.phase;
  const result = state.result;
  const classifyResults = result?.classifyResults;
  const isDisabled = phase === 'submitting' || phase === 'grading';
  const isGraded = phase === 'graded';

  // Unassigned items
  const unassigned = useMemo(
    () => items.filter((item) => !assignments[item.id]),
    [items, assignments]
  );

  // Items per category
  const itemsByCategory = useMemo(() => {
    const map: Record<string, ClassifyItem[]> = {};
    for (const cat of categories) {
      map[cat.id] = [];
    }
    for (const item of items) {
      if (assignments[item.id]) {
        const catId = assignments[item.id];
        if (map[catId]) {
          map[catId].push(item);
        }
      }
    }
    return map;
  }, [categories, items, assignments]);

  const mediaItems: MediaItem[] | null = normaliseMediaRefs(currentStep?.mediaRefsJson);

  const diagramJson = (currentStep as unknown as Record<string, unknown>)?.diagramJson as
    | DiagramJson
    | null
    | undefined;

  if (!currentStep || !snapshot) return null;

  function handleItemTap(itemId: string) {
    if (isDisabled || isGraded) return;

    // If we have an active selection and clicked a classified item,
    // place the active item into that item's category
    if (activeItemId && assignments[itemId]) {
      const targetCategory = assignments[itemId];
      dispatch({
        type: 'UPDATE_ANSWER',
        answer: {
          type: 'classify',
          assignments: { ...assignments, [activeItemId]: targetCategory },
        },
      });
      setActiveItemId(null);
      return;
    }

    // If item is already assigned (no active selection), unassign it
    if (assignments[itemId]) {
      const next = { ...assignments };
      delete next[itemId];
      dispatch({ type: 'UPDATE_ANSWER', answer: { type: 'classify', assignments: next } });
      setActiveItemId(null);
      return;
    }

    // Toggle selection
    setActiveItemId((prev) => (prev === itemId ? null : itemId));
  }

  function handleCategoryTap(categoryId: string) {
    if (isDisabled || isGraded || !activeItemId) return;

    dispatch({
      type: 'UPDATE_ANSWER',
      answer: {
        type: 'classify',
        assignments: { ...assignments, [activeItemId]: categoryId },
      },
    });
    setActiveItemId(null);
  }

  function handleReset() {
    if (isDisabled || isGraded) return;
    dispatch({ type: 'UPDATE_ANSWER', answer: { type: 'classify', assignments: {} } });
  }

  const hasAnyAssignment = Object.keys(assignments).length > 0;

  function getItemResult(itemId: string) {
    if (!classifyResults) return null;
    return classifyResults.find((r) => r.itemId === itemId) ?? null;
  }

  return (
    <StepShell>
      <PromptRenderer
        doc={snapshot.prompt as TiptapNode}
        media={mediaItems}
        diagramJson={diagramJson ?? null}
      />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--f-space-4)',
          opacity: isDisabled ? 0.7 : 1,
          pointerEvents: isDisabled ? 'none' : 'auto',
          fontFamily: 'var(--f-font-sans)',
        }}
      >
        {/* Categories with their items */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: categories.length <= 2 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
            gap: 'var(--f-space-3)',
          }}
        >
          {categories.map((category) => {
            const catItems = itemsByCategory[category.id] ?? [];
            const isActive = activeItemId !== null && !isGraded;

            return (
              <div
                key={category.id}
                role="button"
                tabIndex={isActive ? 0 : -1}
                onClick={() => handleCategoryTap(category.id)}
                onMouseEnter={() => isActive && setHoveredCategoryId(category.id)}
                onMouseLeave={() => setHoveredCategoryId(null)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleCategoryTap(category.id);
                  }
                }}
                style={{
                  padding: 'var(--f-space-3)',
                  borderRadius: 'var(--f-radius-lg)',
                  border: `2px solid ${isActive && hoveredCategoryId === category.id ? 'var(--f-accent)' : 'var(--f-border)'}`,
                  backgroundColor: 'var(--f-bg-sunken)',
                  minHeight: '6rem',
                  cursor: isActive ? 'pointer' : 'default',
                  transition: 'border-color var(--f-duration-fast) ease',
                  position: 'relative',
                  outline: 'none',
                }}
              >
                <AnimatedKbd show={!isGraded && showShortcuts}>
                  {categories.indexOf(category) + 1}
                </AnimatedKbd>
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--f-space-2)',
                    fontSize: 'var(--f-text-xs)',
                    fontWeight: 700,
                    color: 'var(--f-fg-secondary)',
                    marginBottom: 'var(--f-space-2)',
                    textTransform: 'uppercase',
                    letterSpacing: 'var(--f-tracking-normal)',
                  }}
                >
                  {category.label}
                </span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--f-space-1)' }}>
                  {catItems.map((item) => {
                    const itemResult = getItemResult(item.id);
                    const isCorrect = itemResult?.isCorrect;

                    let itemBorder = 'var(--f-accent)';
                    let itemBg = 'var(--f-accent-subtle)';
                    let itemColor = 'var(--f-fg)';

                    if (isGraded && itemResult) {
                      itemBorder = isCorrect ? 'var(--f-success)' : 'var(--f-error)';
                      itemBg = isCorrect ? 'var(--f-success-subtle)' : 'var(--f-error-subtle)';
                      itemColor = isCorrect ? 'var(--f-success-text)' : 'var(--f-error-text)';
                    }

                    const correctCatLabel =
                      isGraded && itemResult && !isCorrect
                        ? categories.find((c) => c.id === itemResult.correctCategoryId)?.label
                        : null;

                    return (
                      <div key={item.id} style={{ display: 'flex', flexDirection: 'column' }}>
                        <button
                          type="button"
                          disabled={isGraded}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleItemTap(item.id);
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 'var(--f-space-1)',
                            padding: 'var(--f-space-1) var(--f-space-2)',
                            borderRadius: 'var(--f-radius-md)',
                            border: `1px solid ${itemBorder}`,
                            backgroundColor: itemBg,
                            color: itemColor,
                            fontSize: 'var(--f-text-sm)',
                            cursor: isGraded ? 'default' : 'pointer',
                            width: '100%',
                            textAlign: 'left',
                          }}
                        >
                          <span>{item.text}</span>
                          {isGraded &&
                            itemResult &&
                            (isCorrect ? <Check size={14} /> : <X size={14} />)}
                        </button>
                        {correctCatLabel && (
                          <span
                            style={{
                              fontSize: '0.65rem',
                              color: 'var(--f-success-text)',
                              marginTop: '1px',
                              paddingLeft: 'var(--f-space-2)',
                            }}
                          >
                            &rarr; {correctCatLabel}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Unassigned items pool */}
        {!isGraded && unassigned.length > 0 && (
          <div>
            <span
              style={{
                display: 'block',
                fontSize: 'var(--f-text-xs)',
                color: 'var(--f-fg-muted)',
                marginBottom: 'var(--f-space-2)',
              }}
            >
              Tap an item, then tap a category to place it
            </span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--f-space-2)' }}>
              {unassigned.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleItemTap(item.id)}
                  onMouseEnter={() => setHoveredItemId(item.id)}
                  onMouseLeave={() => setHoveredItemId(null)}
                  style={{
                    padding: 'var(--f-space-2) var(--f-space-3)',
                    borderRadius: 'var(--f-radius-lg)',
                    border: `2px solid ${activeItemId === item.id ? 'var(--f-accent)' : hoveredItemId === item.id ? 'var(--f-accent)' : 'var(--f-border)'}`,
                    backgroundColor:
                      activeItemId === item.id ? 'var(--f-accent-subtle)' : 'var(--f-bg-raised)',
                    color: 'var(--f-fg)',
                    fontSize: 'var(--f-text-sm)',
                    cursor: 'pointer',
                    transition: 'all var(--f-duration-fast) ease',
                  }}
                >
                  {item.text}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Reset button */}
        <AnimatePresence>
          {!isGraded && hasAnyAssignment && (
            <motion.div
              className="flex items-center gap-2"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
            >
              <button
                type="button"
                disabled={isDisabled}
                onClick={handleReset}
                className="flex items-center gap-1 rounded-lg px-3 py-1.5 transition-opacity cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                style={{
                  fontSize: 'var(--f-text-sm)',
                  color: 'var(--f-fg-secondary)',
                  fontFamily: 'var(--f-font-sans)',
                }}
              >
                <RotateCcw size={14} />
                Reset
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {isGraded && result && <FeedbackDisplay result={result} />}
    </StepShell>
  );
}
